import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Spinner,
  Divider,
  Stack,
} from "@chakra-ui/react";
import { FaSearch } from "react-icons/fa";
import WhiteBgInput from "../WhiteBgInput";
import AudienceList, { audienceTargetingKey } from "./AudienceList";
import AudienceCreationCard from "./AudienceCreationCard";
import { KYDEventIcon, UploadIcon, SocialIcon } from "./AudienceIcons";
import { useFormContext } from "react-hook-form";
import {
  KYDAudienceData,
  CsvAudienceData,
  PageLookalikeAudienceData,
  PageEngagersAudienceData,
  AudienceData,
} from "@common/interfaces/KYDMarketing";
import {
  UseAudiencesState,
  UseAudiencesActions,
  useAudiences,
} from "../hooks/useAudiences";
import CreateCustomAudienceDialog, {
  CreateCustomAudienceDialogContext,
} from "./CreateCustomAudienceDialog/CreateCustomAudienceDialog";
import CreateAudienceFromCsvDialog, {
  CreateAudienceFromCsvDialogContext,
} from "./CreateAudienceFromCsvDialog";
import {
  PageLookalikeAudienceDialog,
  PageLookalikeAudienceDialogContext,
} from "./PageLookalikeAudienceDialog";
import { useDialogWithOptions, DialogProvider } from "src/Common/Dialog";
import { PageLookalikeAudienceFormValues } from "./PageLookalikeAudienceDialog/types";
import { CreateCustomAudienceFormValues } from "./CreateCustomAudienceDialog/types";
import { useEffect, useCallback, useMemo } from "react";

/** Unique key for audiences not yet backed by a Meta id (checkbox + list stability). */
function nextClientAudienceId(kind: string): string {
  return `${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

interface AudienceCardProps {
  fieldName?: string;
  loading?: {
    campaigns: boolean;
    audiences: boolean;
  };
  isEdit?: boolean;
  audiences: AudienceData[];
  pages?: Array<{
    id: string;
    name: string;
  }>;
  instagramAccounts?: Array<{
    id: string;
    username: string;
  }>;
  ad_account_id: string;
  isEmbedded?: boolean;
  title?: string;
}

// Inner component that uses the dialog context
const AudienceCardInner = ({
  fieldName = "custom_audiences",
  loading,
  isEdit,
  pages = [],
  ad_account_id,
  instagramAccounts = [],
  isEmbedded = false,
  title = "Audiences",
}: AudienceCardProps) => {
  const { watch, setValue, register, control } = useFormContext();
  const { open: openCreateAudienceDialog } = useDialogWithOptions(
    CreateCustomAudienceDialogContext
  );
  const { open: openCreateCsvAudienceDialog } = useDialogWithOptions(
    CreateAudienceFromCsvDialogContext
  );
  const { open: openPageLookalikeAudienceDialog } = useDialogWithOptions(
    PageLookalikeAudienceDialogContext
  );

  const { state: audienceState, actions: audienceActions } = useAudiences();

  const audiencesLoading =
    loading?.audiences ?? audienceState.loading?.audiences ?? false;

  const onSearch = (query: string) => {
    audienceActions.search({
      query,
      ad_account_id,
    });
  };

  useEffect(() => {
    if (!ad_account_id) return;
    audienceActions.list({
      ad_account_id: ad_account_id,
      next: false,
    });
  }, [audienceActions.list, ad_account_id]);

  const custom_audiences = watch(fieldName, []) as AudienceData[];
  // Get current audiences for display
  const currentCustomAudiences = useMemo(() => {
    return custom_audiences.map((audience) => {
      if (audience.audience_type === "kyd") {
        const metaAudience = audienceState.audiences?.find(
          (a) => a.external_id === audience.external_id
        );

        return {
          ...metaAudience,
          ...audience,
          id: audience.id,
          external_id: audience.external_id || metaAudience?.external_id,
          subtype: null,
        };
      }

      return audience;
    });
  }, [custom_audiences, audienceState.audiences]);

  // Handle custom audience creation - using watch inside callback to avoid stale closures
  const handleKydCustomAudienceCreated = useCallback(
    (audience: KYDAudienceData) => {
      const currentAudiences = watch(fieldName, []) as AudienceData[];
      setValue(
        fieldName,
        [
          ...currentAudiences,
          {
            id: nextClientAudienceId("kyd_audience"),
            name: audience.name,
            audience_type: "kyd",
            subtype: "kyd",
            created_at: audience.created_at ?? new Date().toISOString(),
            genres: audience.genres,
            artists: audience.artists,
            lookalike: audience.lookalike,
          },
        ],
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [watch, setValue, fieldName]
  );

  // Handle CSV audience creation - using watch inside callback
  const handleCsvAudienceCreated = useCallback(
    (audienceData: {
      id: string;
      name: string;
      description: string;
      file_key: string;
      mapping: string[];
    }) => {
      const currentAudiences = watch(fieldName, []) as AudienceData[];
      const csvAudience: CsvAudienceData = {
        id: audienceData.id ?? nextClientAudienceId("csv_audience"),
        name: audienceData.name,
        description: audienceData.description,
        file_key: audienceData.file_key,
        mapping: audienceData.mapping,
        schema_fields: audienceData.mapping.filter((field) => field !== ""),
        audience_type: "csv",
        created_at: new Date().toISOString(),
      };
      setValue(fieldName, [...currentAudiences, csvAudience], {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [watch, setValue, fieldName]
  );

  // Handle page audience creation - handles both page_engagers and page_lookalike
  const handlePageLookalikeAudienceCreated = useCallback(
    (audienceData: PageLookalikeAudienceFormValues) => {
      const currentAudiences = watch(fieldName, []) as AudienceData[];
      const platformLabel =
        audienceData.pageType === "facebook" ? "Facebook" : "Instagram";

      let newAudience: PageEngagersAudienceData | PageLookalikeAudienceData;

      if (audienceData.audienceMode === "engagers") {
        const pageEngagersAudience: PageEngagersAudienceData = {
          id: nextClientAudienceId("page_engagers"),
          name: audienceData.name || "",
          description: `Page audience from ${platformLabel}`,
          page_type: audienceData.pageType,
          page_id: audienceData.pageId,
          page_name: audienceData.pageName,
          audience_type: "page_engagers",
          created_at: new Date().toISOString(),
        };
        newAudience = pageEngagersAudience;
      } else {
        const pageLookalikeAudience: PageLookalikeAudienceData = {
          id: nextClientAudienceId("page_lookalike"),
          name: audienceData.lookalike?.name || audienceData.name || "",
          description: `Lookalike audience from ${platformLabel} page`,
          page_type: audienceData.pageType,
          page_id: audienceData.pageId,
          page_name: audienceData.pageName,
          lookalike: audienceData.lookalike,
          audience_type: "page_lookalike",
          created_at: new Date().toISOString(),
        };
        newAudience = pageLookalikeAudience;
      }

      setValue(fieldName, [...currentAudiences, newAudience], {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [watch, setValue, fieldName]
  );

  return (
    <>
      <VStack w="full" h="full" spacing={2}>
        <Box
          w="full"
          py={4}
          px={6}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          backgroundColor="white"
        >
          <VStack align="flex-start" flex={1} spacing={4}>
            <VStack w="full" spacing={1} align="flex-start">
              <Text
                fontWeight="bold"
                fontSize="md"
                color="gray.800"
                lineHeight="1.2"
              >
                Custom audience creation
              </Text>
              <Text fontSize="sm" color="gray.600" lineHeight="1.4">
                Create a new audience or reuse one from past campaigns.
              </Text>
            </VStack>
            <VStack w="full" gap={2} align="flex-start">
              <Stack
                w="full"
                h="full"
                spacing={2}
                alignItems="center"
                direction={"column"}
              >
                <AudienceCreationCard
                  icon={<KYDEventIcon />}
                  title="Create Audience from KYD"
                  description="Create a new audience from your KYD events data"
                  onClick={() => openCreateAudienceDialog()}
                  // isDisabled={loading?.campaigns || isEdit}
                />

                <AudienceCreationCard
                  icon={<UploadIcon />}
                  title="Upload Customer Data"
                  description="Upload a CSV with customer emails, phone numbers, or IDs"
                  onClick={() => openCreateCsvAudienceDialog()}
                  // isDisabled={loading?.campaigns || isEdit}
                />
                <AudienceCreationCard
                  icon={<SocialIcon />}
                  title="Create Audience from FB or IG"
                  description="Create a new audience from Facebook or Instagram followers"
                  onClick={() => openPageLookalikeAudienceDialog()}
                  // isDisabled={loading?.campaigns || isEdit}
                />
              </Stack>
            </VStack>
            <Divider borderColor={"gray.200"} />
            <HStack w="full" gap={2} align="stretch">
              <WhiteBgInput
                flex={1}
                lineHeight="23px"
                fontSize="14px"
                placeholder="Search for previously created audiences"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onSearch?.(watch?.("audience_search_query"));
                  }
                }}
                {...(register ? register("audience_search_query") : {})}
              />
              <Button
                size="sm"
                h={10}
                variant="outline"
                leftIcon={
                  audiencesLoading ? <Spinner size="sm" /> : <FaSearch />
                }
                isDisabled={audiencesLoading}
                onClick={() => {
                  onSearch?.(watch?.("audience_search_query"));
                }}
              >
                Search
              </Button>
            </HStack>
            <VStack gap={1} w="full">
              <AudienceList
                audiences={currentCustomAudiences || []}
                control={control}
                fieldName={fieldName}
              />
              <AudienceList
                audiences={
                  audienceState.audiences?.filter((a) => {
                    const metaKey = audienceTargetingKey(a as AudienceData);
                    if (!metaKey) return true;
                    return !currentCustomAudiences?.some(
                      (b) => audienceTargetingKey(b) === metaKey
                    );
                  }) || []
                }
                control={control}
                fieldName={fieldName}
              />
            </VStack>
          </VStack>
        </Box>
      </VStack>

      {/* Dialog Components */}
      <CreateCustomAudienceDialogWrapper
        onCreate={handleKydCustomAudienceCreated}
      />
      <CreateAudienceFromCsvDialogWrapper onSubmit={handleCsvAudienceCreated} />
      <PageLookalikeAudienceDialogWrapper
        onCreate={handlePageLookalikeAudienceCreated}
        pages={pages}
        instagramAccounts={instagramAccounts}
      />
    </>
  );
};

// Wrapper components that connect to dialog context
const CreateCustomAudienceDialogWrapper = ({
  onCreate,
}: {
  onCreate: (audience: KYDAudienceData) => void;
}) => {
  const { isOpen, close } = useDialogWithOptions(
    CreateCustomAudienceDialogContext
  );

  return (
    <CreateCustomAudienceDialog
      onCreate={onCreate}
      isOpen={isOpen}
      close={close}
    />
  );
};

const CreateAudienceFromCsvDialogWrapper = ({
  onSubmit,
}: {
  onSubmit: (audienceData: {
    id: string;
    name: string;
    description: string;
    file_key: string;
    mapping: string[];
  }) => void;
}) => {
  const { isOpen, close } = useDialogWithOptions(
    CreateAudienceFromCsvDialogContext
  );

  return (
    <CreateAudienceFromCsvDialog
      onSubmit={onSubmit}
      isOpen={isOpen}
      close={close}
    />
  );
};

// Wrapper for PageLookalikeAudienceDialog
const PageLookalikeAudienceDialogWrapper = ({
  onCreate,
  pages,
  instagramAccounts,
}: {
  onCreate: (audience: PageLookalikeAudienceFormValues) => void;
  pages?: Array<{
    id: string;
    name: string;
  }>;
  instagramAccounts?: Array<{
    id: string;
    username: string;
  }>;
}) => {
  const { isOpen, close } = useDialogWithOptions(
    PageLookalikeAudienceDialogContext
  );

  return (
    <PageLookalikeAudienceDialog
      onCreate={onCreate}
      isOpen={isOpen}
      close={close}
      pages={pages}
      instagramAccounts={instagramAccounts}
    />
  );
};

// Main component that provides dialog contexts
const AudienceCard = (props: AudienceCardProps) => {
  return (
    <DialogProvider context={CreateCustomAudienceDialogContext}>
      <DialogProvider context={CreateAudienceFromCsvDialogContext}>
        <DialogProvider context={PageLookalikeAudienceDialogContext}>
          <AudienceCardInner {...props} />
        </DialogProvider>
      </DialogProvider>
    </DialogProvider>
  );
};

export default AudienceCard;
